<script lang="ts">
  import {fade} from 'svelte/transition';
  import Dropzone, {type DropzoneOptions, type DropzoneFile} from 'dropzone';
  import InlineSVG from 'svelte-inline-svg';

  import infoIcon from '../../assets/icons/info.svg';
  import type {ImageFile} from '../../types/project';

  import dataUrlToBlob from '../../utils/data-url-to-blob';

  import Note from '../note/Note.svelte';
  import Tooltip from '../tooltip/Tooltip.svelte';

  import uploadIcon from '../../assets/icons/upload.svg';
  import helpIcon from '../../assets/icons/help.svg';

  export let label = '';
  export let id = '';
  export let files: Array<ImageFile> = [];

  export let captions: Record<string, string> = {};
  export let required = false;

  let dropzoneElement: HTMLElement | null = null;
  let dropzone: Dropzone | null = null;
  let filesToProcess: Array<string> = [];
  let uploadError: string | Error = '';

  const dropzoneOptions: DropzoneOptions = {
    url: '/',
    uploadMultiple: true,
    paramName: id,
    clickable: true,
    acceptedFiles: 'image/*',
    autoProcessQueue: false
  };

  // Adds an image to the files array and validates the image size
  async function handleAddFile(file: DropzoneFile) {
    // @ts-ignore: Properties 'width' and 'height' do not exist on type 'DropzoneFile'.
    const {name, width, height, dataURL} = file;

    if (!dataURL) {
      return;
    }

    const blob = await dataUrlToBlob(dataURL);

    const image: ImageFile = {
      type: 'image',
      name,
      width,
      height,
      url: dataURL,
      blob,
      onDelete: () => dropzone?.removeFile(file)
    };

    files = [...files, image].sort((fileA, fileB) =>
      fileA.name.localeCompare(fileB.name)
    );
    captions[file.name] = '';

    filesToProcess = filesToProcess.filter(name => name !== file.name);
  }

  // Reset error message and show the progress bar
  function startFileProcessing(file: DropzoneFile) {
    uploadError = '';
    filesToProcess = [...filesToProcess, file.name];
  }

  // Removes an image from the files array
  function handleRemoveFile(file: DropzoneFile) {
    files = files.filter(({name}) => name !== file.name);
  }

  // Create dropzone for image upload
  $: {
    if (dropzoneElement) {
      dropzone = new Dropzone(dropzoneElement, dropzoneOptions);

      // Event listener to get the added images
      dropzone.on('thumbnail', handleAddFile);
      // Event listener to remove an image from the list
      dropzone.on('removedfile', handleRemoveFile);
      // Event listener to reset error message and show the progress bar
      dropzone.on('addedfile', startFileProcessing);
      // Event listener to catch errors
      dropzone.on('errormultiple', (_, errorMessage) => {
        uploadError = errorMessage;
      });
    }
  }
</script>

<style lang="postcss">
  .container {
    display: grid;
    gap: 0.25rem;
    margin-bottom: 1.5rem;
  }

  .container > div {
    position: relative;
  }

  .container > div:nth-child(2) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  @media (min-width: 768px) {
    .container {
      grid-template-columns: 1fr 2fr 1fr;
      align-items: start;
      gap: 1.5rem;
    }

    .container > div:first-child {
      display: flex;
      align-items: center;
      justify-content: end;
      min-height: var(--ubilabs-input-height);
    }
  }

  .upload {
    position: relative;
    height: 8rem;
    border-radius: var(--ubilabs-border-radius-small);
    background-color: var(--ubilabs-gray-light);
  }

  .dropzone {
    height: 100%;
  }

  :global(.dz-drag-hover) {
    border: 1px solid var(--ubilabs-blue);
    background-color: var(--ubilabs-white);
    outline: none;
  }

  .dz-message {
    display: flex;
    flex-grow: 1;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    height: 100%;
    margin: 0;
    color: var(--ubilabs-gray-dark-2);
    cursor: default;
  }

  .dropzone :global(.dz-preview) {
    display: none;
  }

  .progress {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 0.25rem;
    overflow: hidden;
    border-bottom-left-radius: var(--ubilabs-border-radius-small);
    border-bottom-right-radius: var(--ubilabs-border-radius-small);
    pointer-events: none;
  }

  .progressBar {
    display: flex;
    width: 100%;
    height: 100%;
    background-color: var(--ubilabs-gray);
    transition: all 300ms ease-linear;
  }

  .progressBar:before {
    content: '';
    position: absolute;
    height: 100%;
    background-color: var(--ubilabs-gray-dark-1);
    -webkit-animation: running-progress 2s cubic-bezier(0.35, 0.35, 0.5, 1)
      infinite;
    animation: running-progress 2s cubic-bezier(0.35, 0.35, 0.5, 1) infinite;
  }

  @-webkit-keyframes running-progress {
    0% {
      left: 0px;
      right: 100%;
    }
    50% {
      left: 25%;
      right: 0%;
    }
    100% {
      left: 100%;
      right: 0;
    }
  }
  @keyframes running-progress {
    0% {
      left: 0px;
      right: 100%;
    }
    50% {
      left: 25%;
      right: 0%;
    }
    100% {
      left: 100%;
      right: 0;
    }
  }

  .errorMessage {
    margin: -0.5rem 0.75rem;
    color: var(--ubilabs-error-color);
  }

  @media (min-width: 768px) {
    .errorMessage {
      display: flex;
      justify-content: center;
    }

    .errorMessage > small {
      width: 50%;
    }
  }
</style>

<div class="container">
  <div>
    {#if label}
      <label for={id}>{label}{required ? '*' : ''}</label>
    {/if}
  </div>
  <div>
    <div class="upload">
      <div class="dropzone" bind:this={dropzoneElement}>
        <div class="dz-message">
          <InlineSVG src={uploadIcon} />
          Drop files to upload or click here
        </div>
      </div>

      {#if filesToProcess.length}
        <div class="progress" out:fade|local>
          <div class="progressBar" />
        </div>
      {/if}
    </div>

    <Note variant="info" iconPosition="right">
      The image should be 1280&nbsp;×&nbsp;720 pixels in size for desktop
      screenshots or 427&nbsp;×&nbsp;720 pixels in size for mobile screenshots.
      <span slot="icon">
        <Tooltip position="left" openOnClick={true}>
          <div slot="trigger">
            <InlineSVG src={helpIcon} />
          </div>

          <div slot="content">
            <p>Open the project in your Chrome browser.</p>
            <p>
              Open the Developer Tools (COMMAND + OPTION + I) and toggle the
              device toolbar (COMMAND + SHIFT + M).
            </p>
            <p>
              Select the device “Responsive” and resize the page area to
              1280&nbsp;×&nbsp;720 for desktop or 427&nbsp;×&nbsp;720 for mobile
              screenshots. You can also save these dimensions if you click on
              the edit option in the dimensions flyout.
            </p>
            <p>
              To make a screenshot of the page area, open the options menu again
              and click "Capture screenshot".
            </p>
            <p>
              You already have a screenshot? You can crop images right here by
              clicking the crop button on the image thumbnail.
            </p>
          </div>
        </Tooltip>
      </span>
    </Note>

    <Note variant="info">
      <span slot="icon"><InlineSVG src={infoIcon} /></span>
      New images are not saved until the form is submitted, so do not change or delete
      your local image files unless you saved this project.
    </Note>
  </div>
</div>

{#if uploadError}
  <div class="errorMessage">
    <small>{uploadError}</small>
  </div>
{/if}
